import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from './button';

const PAGE_SIZE_OPTIONS = [25, 50, 100, 200, 500, 1000];

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  totalCount: number;
  itemsPerPage: number;
  /**
   * CR-7279: rows the server actually put on a page. The list paginators
   * now honour every size the Rows control offers (up to 1000), so this
   * normally equals `itemsPerPage`; if an endpoint still clamps, the page
   * count, the "Showing x-y" range AND the Rows select all follow the
   * served size — the control never claims a number the page does not hold.
   */
  servedPerPage?: number;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (size: number) => void;
  itemLabel?: string; // e.g. "deliveries", "NCRs", "lines"
  /** Override the Rows dropdown choices (default 25-1000, the sizes every
   * list paginator serves in full). */
  pageSizeOptions?: number[];
}

export function Pagination({
  currentPage,
  totalPages,
  totalCount,
  itemsPerPage,
  servedPerPage,
  onPageChange,
  onPageSizeChange,
  itemLabel = 'items',
  pageSizeOptions = PAGE_SIZE_OPTIONS,
}: PaginationProps) {
  // Null safety - ensure values are defined
  const safeCurrentPage = currentPage || 1;
  const safeTotalPages = totalPages || 1;
  const safeTotalCount = totalCount || 0;
  const safeItemsPerPage = itemsPerPage || 10;
  const rowsOnPage = servedPerPage && servedPerPage > 0 ? Math.min(servedPerPage, safeItemsPerPage) : safeItemsPerPage;
  // The select shows the size in force. A clamped size is not in the
  // standard list, so it is added rather than leaving the control blank.
  const sizeOptions = pageSizeOptions.includes(rowsOnPage)
    ? pageSizeOptions
    : [...pageSizeOptions, rowsOnPage].sort((a, b) => a - b);

  if (safeTotalPages <= 1) return null;

  const startItem = (safeCurrentPage - 1) * rowsOnPage + 1;
  const endItem = Math.min(safeCurrentPage * rowsOnPage, safeTotalCount);

  return (
    <div className="flex items-center justify-between border-t pt-4 mt-4">
      <div className="flex items-center gap-3 text-sm text-foreground">
        {onPageSizeChange && (
          <div className="flex items-center gap-1">
            <span className="text-xs">Rows:</span>
            <select
              value={rowsOnPage}
              onChange={(e) => onPageSizeChange(Number(e.target.value))}
              className="h-7 text-xs border rounded px-1 bg-background"
              data-testid="rows-per-page"
            >
              {sizeOptions.map((size) => (
                <option key={size} value={size}>{size}</option>
              ))}
            </select>
          </div>
        )}
        <span>
          Page {safeCurrentPage} of {safeTotalPages} • {safeTotalCount.toLocaleString()} total {itemLabel}
          <span className="ml-2 text-xs">
            (Showing {startItem.toLocaleString()}-{endItem.toLocaleString()})
          </span>
        </span>
      </div>
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(safeCurrentPage - 1)}
          disabled={safeCurrentPage === 1}
        >
          <ChevronLeft className="h-4 w-4" />
          Previous
        </Button>
        <div className="flex items-center gap-1">
          {Array.from({ length: Math.min(5, safeTotalPages) }, (_, i) => {
            let pageNum: number;
            if (safeTotalPages <= 5) {
              pageNum = i + 1;
            } else if (safeCurrentPage <= 3) {
              pageNum = i + 1;
            } else if (safeCurrentPage >= safeTotalPages - 2) {
              pageNum = safeTotalPages - 4 + i;
            } else {
              pageNum = safeCurrentPage - 2 + i;
            }
            return (
              <Button
                key={pageNum}
                variant={safeCurrentPage === pageNum ? 'default' : 'outline'}
                size="sm"
                onClick={() => onPageChange(pageNum)}
                className="w-8 h-8 p-0"
              >
                {pageNum}
              </Button>
            );
          })}
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(safeCurrentPage + 1)}
          disabled={safeCurrentPage === safeTotalPages}
        >
          Next
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
